import { TZ } from '@/pages/calendar/time';
import type { AppAccountRef, AppLinkMethod } from '@/features/patients/types';

/** RL-1118: how the app account was linked (StaffAppAccountDto.method). */
export const APP_LINK_METHOD_LABELS: Record<AppLinkMethod, string> = {
  ekyc: 'eKYC in the app',
  mydigital_id: 'MyDigital ID',
  clinic_check: 'Checked at the clinic',
  self_registration: 'Registered in the app (only the phone was proved)',
  phone_second_check: 'Phone and a second check',
  test_account: 'Test account',
};

export const appLinkMethodLabel = (method: string) =>
  APP_LINK_METHOD_LABELS[method as AppLinkMethod] ?? method;

/**
 * What the API removes from the record when an app account is unlinked
 * (counter eKYC "not theirs", RL-1118, and the staff unlink, RL-1184: one
 * shared path), by how the account was linked.
 */
export const appAccountClearedText = (method: string) =>
  method === 'self_registration'
    ? 'The phone and email the app entered on this record are removed, and so are contacts added in the app.'
    : method
      ? 'Contacts added in the app are removed (the phone and email on the record stay).'
      : 'Contacts added in the app are removed, and if the app created this record, the phone and email it entered too.';

/** "20 Oct 2026, 10:01" in branch time. */
export const appAccountTime = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    timeZone: TZ,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

/** "20 Oct 2026" in branch time. */
export const appAccountDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', {
    timeZone: TZ,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

/**
 * The `appAccount` extension of 422 APP_ACCOUNT_CONFIRMATION_REQUIRED
 * ({ phone, method, linkedAt }); blanks when the problem did not carry it.
 */
export function appAccountFromProblem(
  problem: Record<string, unknown> | undefined,
): AppAccountRef {
  const raw: unknown = problem?.appAccount;
  const a = (raw && typeof raw === 'object' ? raw : {}) as Record<
    string,
    unknown
  >;
  return {
    phone: typeof a.phone === 'string' ? a.phone : null,
    method: typeof a.method === 'string' ? a.method : '',
    linkedAt: typeof a.linkedAt === 'string' ? a.linkedAt : '',
  };
}
